import { Component } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { withSnackbar, WithSnackbarProps } from '@robocorp/components';
import { IconBug } from '@robocorp/icons/iconic';
// eslint-disable-next-line import/no-unresolved
import type { OpenEvent } from 'ws';
import { generateInterval } from '../../utils/websocketHelper';
import { websocketStatus } from '../../constants';
import { getAuthToken } from '~/utils/authHelpers';

const isInDevMode = () => import.meta.env.DEV || window.location.hostname.includes('.dev');

type OwnProps = {
  url: string;
  shouldReconnect?: boolean;
  onMessage?: (event: MessageEvent, actions: Record<string, () => void>) => void;
  onClose?: (event: CloseEvent) => void;
  onOpen?: (event: OpenEvent, token: string) => void;
  queryParams: Record<string, string>;
  name: string;
  connectionErrorMessage: string;
  connectionSuccessMessage: string;
  logColor: string;
  tokenCallback?: () => Promise<string | undefined>;
  invalidateTokenOnConnect?: boolean;
  token?: string;
};

type Props = OwnProps & WithSnackbarProps & WithTranslation;

class BaseSocket extends Component<Props> {
  private websocket?: WebSocket;

  private reconnectionAttempts: number;

  private tokenAttempts: number;

  private initializing: boolean;

  private tokenTimer?: number;

  private reconnectionTimer?: number;

  private initialLoad: boolean;

  private heartbeatInterval: number;

  private heartbeat?: number;

  private unmounting: boolean;

  private tokenIsStillValid: boolean;

  private token?: string;

  private snackbarKey?: string;

  private websocketOpen: boolean;

  static defaultProps = {
    shouldReconnect: true,
    onMessage: undefined,
    onClose: undefined,
    onOpen: undefined,
    queryParams: {},
    logColor: 'black',
    tokenCallback: undefined,
    invalidateTokenOnConnect: false,
    token: '',
  };

  constructor(props: Props) {
    super(props);

    this.websocket = undefined;
    this.reconnectionAttempts = 0;
    this.tokenAttempts = 0;
    this.initializing = false;
    this.tokenTimer = undefined;
    this.reconnectionTimer = undefined;
    this.initialLoad = true; // used to hide "control channel offline" during initial connect attempt
    this.heartbeatInterval = 60000; // ms for heartbeat
    this.heartbeat = undefined;
    this.unmounting = false; // for detecting if socket onclose is from unmount or "real" disconnect
    this.tokenIsStillValid = true; // used for detecting if the initial connection token is still valid when reconnecting
    this.token = '';
    this.websocketOpen = false;
  }

  async componentDidMount() {
    await this.setupWebSocket('Mount');
  }

  componentDidUpdate() {
    const { addSnackbar, closeSnackbar, connectionErrorMessage, connectionSuccessMessage } =
      this.props;
    const { websocketOpen, snackbarKey } = this;

    if (!websocketOpen && !snackbarKey && !this.initialLoad && isInDevMode()) {
      const key = addSnackbar({
        message: `Internal: ${connectionErrorMessage}`,
        icon: IconBug,
        variant: 'danger',
        persist: true,
      });

      this.snackbarKey = key;
    }

    if (snackbarKey && websocketOpen) {
      this.snackbarKey = undefined;
      closeSnackbar(snackbarKey);
      addSnackbar({ message: connectionSuccessMessage, variant: 'success' });
    }
  }

  componentWillUnmount() {
    const { closeSnackbar } = this.props;
    const { snackbarKey } = this;

    if (snackbarKey) closeSnackbar(snackbarKey);

    clearTimeout(this.reconnectionTimer);
    clearTimeout(this.tokenTimer);
    clearInterval(this.heartbeat);

    this.logger('Unmounted');
    this.unmounting = true;
    if (this.websocket) this.websocket.close();
  }

  setupWebSocket = async (source: string) => {
    this.logger('Websocket setup initialized by:', source);

    if (!this.initializing && !this.websocket) {
      this.initializing = true;
      const token = await this.tryGetToken();

      if (token) {
        this.tokenAttempts = 0;
        const socket = new WebSocket(this.buildUrl());

        socket.onopen = (event) => {
          this.onOpen(event as unknown as OpenEvent);

          /**
           * websocketOpen value isn't kept in state
           * Manually forcing update to clear existing toast messages about being disconnected
           */
          if (!this.initialLoad) {
            this.forceUpdate();
          }
        };

        socket.onmessage = (event) => {
          this.onMessage(event);
        };

        socket.onclose = (event) => {
          this.onClose(event);
        };

        this.initializing = false;
        this.websocket = socket;
      } else {
        this.logger('Setup aborted, no token');
        this.initializing = false;
      }
    } else {
      this.logger('Websocket setup already in progress');
    }
  };

  handleReconnect = () => {
    const time = generateInterval(this.reconnectionAttempts);
    this.logger(
      `Attempting socket reconnection, attempt: ${this.reconnectionAttempts + 1}, ms: ${time}`,
    );
    this.reconnectionTimer = window.setTimeout(async () => {
      this.reconnectionAttempts += 1;
      await this.setupWebSocket('Reconnection mechanism');
    }, time);
  };

  logger = (...params: string[]) => {
    const { name, logColor } = this.props;
    if (isInDevMode()) {
      // eslint-disable-next-line
      console.log(`%c[${name}]`, `color: ${logColor}`, ...params);
    }
  };

  onHeartbeat = () => {
    this.sendMessage(JSON.stringify({ action: 'heartbeat' }));
  };

  getToken = async () => {
    // First check if token has been given when mounting, use that if so
    // Didn't want to use getAuth0Token as default value for tokenCallback to get proper debug logging
    const { tokenCallback, token } = this.props;

    if (token && this.tokenIsStillValid) {
      this.logger('Using given token');
      return token;
    }

    if (tokenCallback) {
      this.logger('Using custom token callback');
      return tokenCallback();
    }

    this.logger('No token or custom callback given, using Auth0');
    return getAuthToken();
  };

  tryGetToken = async () => {
    let token;

    try {
      token = await this.getToken();
    } catch (e) {
      const time = generateInterval(this.tokenAttempts);
      this.logger(
        `Socket failed to get token, retrying setup in ${time} ms`,
        (e as Error).toString(),
      );
      this.tokenTimer = window.setTimeout(async () => {
        this.initializing = false;
        this.tokenAttempts += 1;
        await this.setupWebSocket('Token retrieval failed');
      }, time);
    }

    this.token = token;
    return token;
  };

  buildQueryParamString = () => {
    const { queryParams } = this.props;
    let paramString = '';

    const params = {
      ...queryParams,
      version: encodeURIComponent(import.meta.env.VITE_BUILD_INFO || ''),
    };

    // eslint-disable-next-line
    for (let [key, value] of Object.entries(params)) {
      paramString = `${paramString}&${key}=${value}`;
    }

    return paramString.slice(1, paramString.length);
  };

  buildUrl = () => {
    const { url } = this.props;
    return `${url}?${this.buildQueryParamString()}`;
  };

  setupHeartbeat = () => {
    this.heartbeat = window.setInterval(this.onHeartbeat, this.heartbeatInterval);
  };

  clearHeartbeat = () => {
    clearInterval(this.heartbeat);
  };

  sendMessage = (message: string) => {
    if (this.websocket) {
      this.logger('Sending to socket:', message);
      this.websocket.send(message);
    }
  };

  setConnectionAsOpen = () => {
    this.websocketOpen = true;
    this.reconnectionAttempts = 0;
  };

  // eslint-disable-next-line
  onClose = (event: CloseEvent) => {
    const { onClose } = this.props;
    // https://developer.mozilla.org/en-US/docs/Web/API/WebSockets_API/Writing_WebSocket_client_applications
    // If an error occurs while attempting to connect, first a simple event with
    // the name error is sent to the WebSocket object (thereby invoking its onerror handler),
    // and then the CloseEvent is sent to the WebSocket object (thereby invoking its onclose
    // handler) to indicate the reason for the connection's closing.

    // at this point we have already tried to connect at least once -> show error
    // about control channel being offline.
    this.initialLoad = false;
    this.logger(
      `Socket closed: ${event.reason || event.code}, clean disconnect: ${event.wasClean}`,
    );
    this.clearHeartbeat();
    if (onClose) onClose(event);
    const { shouldReconnect } = this.props;

    this.logger('Socket status:', websocketStatus[this.websocket?.readyState as 0 | 1 | 2 | 3]);
    this.websocket = undefined;

    if (!this.unmounting) {
      this.websocketOpen = false;
      if (shouldReconnect) {
        this.logger('Socket configured to reconnect. Proceeding...');
        this.handleReconnect();
      } else {
        this.logger('Socket not configured to reconnect. Socket terminated');
      }
    } else {
      this.logger('Component unmounted, not reconnecting. Socket terminated');
    }
  };

  onOpen = (event: OpenEvent) => {
    const { onOpen, invalidateTokenOnConnect } = this.props;
    this.logger('Socket opened');

    this.setupHeartbeat();

    if (invalidateTokenOnConnect && this.tokenIsStillValid) {
      this.logger('Invalidating used token');
      this.tokenIsStillValid = false;
    }

    if (onOpen) {
      // If custom onOpen handler is used, let it decide whether we want
      // to mark the socket as "open" or whether additional stuff
      // like authentication needs to happen first
      onOpen(event, this.token as string);
    } else {
      // Default behavior is to assume connection is fully open.
      this.setConnectionAsOpen();
    }
  };

  onMessage = (event: MessageEvent) => {
    const { onMessage } = this.props;

    // Reset heartbeat timer when receiving a message
    this.clearHeartbeat();
    this.setupHeartbeat();

    const data = JSON.parse(event.data);
    // this.logger('Data from socket:', data);
    if (onMessage && data) {
      // Allow custom onMessage handler to perform actions on this socket,
      // for example set connection state as open once "auth complete" message
      // is received.
      const actions = {
        setConnectionAsOpen: this.setConnectionAsOpen,
      };

      onMessage(data, actions);
    }
  };

  render() {
    return <></>;
  }
}

const translated = withTranslation()(BaseSocket);
export default withSnackbar(translated);
